/**
 * What the creator sees of a turn's work: short, plain progress lines ("Reading the full email with the
 * agency"), never the harness underneath (tool names, docs lookups, code, retries, errors).
 *
 * Tool calls that failed are dropped: the agent nearly always recovers or tries another way, and the
 * outcome is what matters. Developers see every call, its code and its error in the dev harness
 * (/dashboard/dev/agent-runs), which reads the raw run trace, not this.
 */
import type { UiRun, UiToolCall } from './reducer'

export interface ProgressStep {
    id: string
    label: string
    state: 'active' | 'done'
    /** When the step's (latest) call started, for the live "· 42s" on a long step. */
    startedAt?: string
    /** What the agent told the creator as it took this step ("Same numbers as the plan. Applying now."). */
    notes?: string[]
}

/**
 * The `desktop` tool's lines, one per action (capture_screen / open_url / read_clipboard). The
 * tool waits on the creator's OK, so the line says it's asking. Keys are the backend's
 * label_for() texts, current and earlier.
 */
const DESKTOP_LABELS: Record<string, string> = {
    'Asking to see your screen': 'Asking to see your screen',
    'Looking at your screen': 'Asking to see your screen',
    'Asking to open a link': 'Asking to open a link',
    'Opening the link': 'Asking to open a link',
    'Asking to read your clipboard': 'Asking to read your clipboard',
    'Reading your clipboard': 'Asking to read your clipboard',
}

/** Fixed tools: what each one means to the creator. `null` = not worth a line (shown elsewhere or internal). */
const TOOL_LABELS: Record<string, ((t: Pick<UiToolCall, 'label'>) => string | null) | string | null> = {
    research: (t) => t.label || 'Looking through your workspace', // the server names the topic
    sdk_search: 'Planning the next steps',
    sdk_docs: 'Planning the next steps',
    todo: null, // the plan has its own UI
    ask_user: null, // the question card says it
    navigate: 'Opening it for you',
    edit_composer: 'Editing your draft',
    start_job: 'Setting up a background job',
    spawn: (t) => (/scout/i.test(t.label) ? 'Looking into a few things at once' : 'Working on a few things at once'),
    computer: 'Working in the browser',
    browser: (t) => t.label || 'Working in the browser',
    propose_browser_task: 'Preparing a browser task',
    // The creator's computer, through the desktop app: the backend names the action (label_for).
    desktop: (t) => DESKTOP_LABELS[t.label] ?? 'Asking to use your computer',
}

/** Words that mean the label is about the machinery, not the creator's work. */
const MACHINERY = /\b(sdk|api|endpoint|schema|python|code|script|sandbox|scratch|variables?|functions?|methods?|json|regex|sql|quer(y|ies)|traceback|exceptions?|stack ?trace|imports?|cache|lh\.|\.py|\.md)\b/i
const RECOVERY = /\b(errors?|retry|retrying|re-?try|debug(ging)?|fix(ing)? (the )?(call|bug|query|code|error)|failed|failure|workaround)\b/i
const DOCS = /\b(docs?|documentation|signatures?|reference|how to (call|use))\b/i
const PLAYBOOK = /\b(playbooks?|skills?|style ?guide|memory|memories|voice (notes?|files?|guide|profile))\b/i
const IDS = /\b[a-z_]+:[a-z0-9][a-z0-9-]{5,}\b|\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b|`[^`]*`/gi
/** Trailing nouns that only describe the data shape ("email text", "thread data"). */
const SHAPE_TAIL = /\s+(text|data|rows?|records?|ids?|objects?|json|payload|fields?|contents?|body)$/i

/** First word → present participle. Covers what the model actually writes in run labels. */
const GERUND: Record<string, string> = {
    read: 'Reading', reread: 'Re-reading', 're-read': 'Re-reading', find: 'Finding', found: 'Finding',
    check: 'Checking', checked: 'Checking', look: 'Looking', looked: 'Looking', load: 'Pulling up',
    loaded: 'Pulling up', fetch: 'Pulling up', fetched: 'Pulling up', pull: 'Pulling up', pulled: 'Pulling up',
    get: 'Getting', got: 'Getting', gather: 'Gathering', gathered: 'Gathering', collect: 'Collecting',
    collected: 'Collecting', draft: 'Drafting', drafted: 'Drafting', write: 'Writing', wrote: 'Writing',
    rewrite: 'Rewriting', rewrote: 'Rewriting', revise: 'Revising', revised: 'Revising', edit: 'Editing',
    edited: 'Editing', create: 'Creating', created: 'Creating', make: 'Making', made: 'Making', build: 'Building',
    built: 'Building', update: 'Updating', updated: 'Updating', search: 'Searching', searched: 'Searching',
    list: 'Looking at', listed: 'Looking at', inspect: 'Looking at', inspected: 'Looking at', view: 'Looking at',
    review: 'Reviewing', reviewed: 'Reviewing', compare: 'Comparing', compared: 'Comparing', count: 'Counting',
    counted: 'Counting', calculate: 'Calculating', calculated: 'Calculating', compute: 'Working out',
    computed: 'Working out', summarize: 'Summarizing', summarized: 'Summarizing', scan: 'Scanning',
    scanned: 'Scanning', verify: 'Double-checking', verified: 'Double-checking', confirm: 'Confirming',
    confirmed: 'Confirming', save: 'Saving', saved: 'Saving', add: 'Adding', added: 'Adding', move: 'Moving',
    moved: 'Moving', mark: 'Marking', marked: 'Marking', set: 'Setting', schedule: 'Scheduling',
    scheduled: 'Scheduling', prepare: 'Preparing', prepared: 'Preparing', match: 'Matching', matched: 'Matching',
    identify: 'Identifying', identified: 'Identifying', open: 'Opening', opened: 'Opening', analyze: 'Analyzing',
    analyzed: 'Analyzing', extract: 'Pulling out', extracted: 'Pulling out', delete: 'Removing',
    deleted: 'Removing', remove: 'Removing', removed: 'Removing', link: 'Linking', linked: 'Linking',
    attach: 'Attaching', attached: 'Attaching', send: 'Preparing', sent: 'Preparing', sort: 'Sorting',
    sorted: 'Sorting', filter: 'Narrowing down', filtered: 'Narrowing down', group: 'Grouping',
    grouped: 'Grouping', plan: 'Planning', planned: 'Planning', organize: 'Organizing', organized: 'Organizing',
    tag: 'Tagging', tagged: 'Tagging', archive: 'Archiving', archived: 'Archiving', assign: 'Assigning',
    assigned: 'Assigning', note: 'Noting', noted: 'Noting', research: 'Researching', researched: 'Researching',
    estimate: 'Estimating', estimated: 'Estimating', apply: 'Applying', applied: 'Applying', fill: 'Filling in',
    filled: 'Filling in', generate: 'Putting together', generated: 'Putting together', compose: 'Writing',
    composed: 'Writing', reply: 'Replying', replied: 'Replying', follow: 'Following', followed: 'Following',
}
/** "agency email" reads better as "email with the agency". */
const PARTY_EMAIL = /^(the )?((?:full|latest|last|most recent|original|whole) )?(agency|brand|client|manager|sponsor|partner|publicist|agent|team) (email|thread|reply|message)s?$/i
/** After the verb, these read better with "the" ("Reading the full email"). */
const WANTS_ARTICLE = /^(full|latest|last|most|current|original|whole|entire|new|recent|newest|oldest|next|first)\b/i
const HAS_DETERMINER = /^(the|a|an|your|their|this|these|that|those|all|any|each|every|some|my|his|her|its|our)\b/i

function sentenceCase(s: string): string {
    return s ? s[0].toUpperCase() + s.slice(1) : s
}

/** The creator-facing rewrite of a model-written step label ("Read full agency email text"). */
export function humanizeLabel(raw: string | null | undefined): string {
    let s = (raw ?? '').replace(IDS, ' ').replace(/[“”"']/g, '').replace(/\s+/g, ' ').trim().replace(/[.…:;]+$/, '')
    if (!s || /^working in the workspace$/i.test(s)) return 'Working on it'
    if (RECOVERY.test(s)) return 'Taking another look'
    if (PLAYBOOK.test(s)) {
        if (/\bpitch/i.test(s)) return 'Thinking about how to write the pitch'
        if (/\b(draft|email|reply|replies|message|follow[- ]?up)/i.test(s)) return 'Thinking about how to draft the email'
        if (/\binvoice/i.test(s)) return 'Thinking about the invoice'
        return 'Thinking it through'
    }
    if (DOCS.test(s)) return 'Planning the next steps'
    if (MACHINERY.test(s)) return 'Working on it'

    const words = s.split(' ')
    const first = words[0].toLowerCase()
    let verb = GERUND[first]
    if (!verb && /ing$/i.test(first)) verb = sentenceCase(words[0])
    if (!verb) return sentenceCase(s.replace(SHAPE_TAIL, ''))
    // "Look up" / "Look into" keep their particle.
    let rest = words.slice(1).join(' ')
    if (/^(up|into|over|through|at|for)\b/i.test(rest) && (first === 'look' || first === 'looked')) {
        verb = 'Looking'
    }
    rest = rest.replace(SHAPE_TAIL, '')
    const party = rest.match(PARTY_EMAIL)
    if (party) rest = `${party[2] ?? ''}${party[4].toLowerCase()} with the ${party[3].toLowerCase()}`
    if (WANTS_ARTICLE.test(rest) && !HAS_DETERMINER.test(rest)) rest = `the ${rest}`
    return rest ? `${verb} ${rest}` : verb
}

/** The line for one tool call, or null when the call isn't worth showing. */
export function friendlyLabel(t: Pick<UiToolCall, 'tool' | 'label'>): string | null {
    if (t.tool in TOOL_LABELS) {
        const m = TOOL_LABELS[t.tool]
        return typeof m === 'function' ? m(t) : m
    }
    return humanizeLabel(t.label)
}

/**
 * Lines that say the agent is busy but not on what. Each shows at most once a turn: repeated, they read as a
 * column of "Planning the next steps / Working on it" that tells the creator nothing.
 */
const GENERIC = new Set(['Planning the next steps', 'Working on it', 'Taking another look', 'Thinking it through',
                         'Going over what I found'])

/**
 * A step note as the creator sees it: the agent's own words to them, minus any sentence about the machinery
 * (code, docs, retries, errors), ids or markdown. Null when nothing is left.
 */
export function friendlyNote(raw: string | null | undefined): string | null {
    const text = (raw ?? '').replace(IDS, ' ').replace(/\*\*|__|^#+\s*/gm, '').replace(/\s+/g, ' ').trim()
    if (!text) return null
    const kept = text.replace(/([.!?])\s+/g, '$1\n').split('\n')
        .filter((x) => x && !MACHINERY.test(x) && !RECOVERY.test(x) && !DOCS.test(x) && !PLAYBOOK.test(x))
    return kept.length ? kept.join(' ') : null
}

/**
 * The visible steps of one turn. Failed calls are left out; consecutive calls that read the same are
 * merged, so retries and multi-part lookups show as one calm line, and a generic line already shown this
 * turn isn't added again. A call's note goes with the next step shown (or the last one), so a note on a
 * hidden or merged call isn't lost.
 */
export function turnSteps(tools: UiToolCall[]): ProgressStep[] {
    const out: ProgressStep[] = []
    let notes: string[] = []
    const attach = (s: ProgressStep) => {
        if (notes.length) s.notes = [...(s.notes ?? []), ...notes]
        notes = []
    }
    for (const t of tools) {
        const note = friendlyNote(t.note)
        if (note) notes.push(note)
        if (t.status === 'error') continue
        const label = friendlyLabel(t)
        if (!label) continue
        const state = t.status === 'running' ? 'active' : 'done'
        const prev = out[out.length - 1]
        if (prev && prev.label === label) {
            if (state === 'active') Object.assign(prev, { state, startedAt: t.startedAt })
            attach(prev)
            continue
        }
        if (GENERIC.has(label) && out.some((s) => s.label === label)) continue
        const step: ProgressStep = { id: t.callId, label, state, startedAt: t.startedAt }
        attach(step)
        out.push(step)
    }
    if (out.length) attach(out[out.length - 1])
    return out
}

/** The agent's latest note this turn (for a collapsed view), or null. */
export function latestNote(tools: UiToolCall[]): string | null {
    for (let i = tools.length - 1; i >= 0; i--) {
        const note = friendlyNote(tools[i].note)
        if (note) return note
    }
    return null
}

/**
 * "42s" / "1m 05s" for a step that has been running a while, or null while it's still quick (a steady
 * count on a long step says the agent isn't stuck; on a short one it's noise).
 */
export function stepElapsed(step: ProgressStep | undefined, now: number, after = 8): string | null {
    if (!step || step.state !== 'active' || !step.startedAt) return null
    const secs = Math.floor((now - Date.parse(step.startedAt)) / 1000)
    if (!Number.isFinite(secs) || secs < after) return null
    return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${String(secs % 60).padStart(2, '0')}s`
}

/**
 * The line shown while the agent works. It holds: the step in flight, else the latest step that ran (so the
 * line stays put between steps instead of going blank), else the plan's current item, else "Thinking".
 */
export function liveHeadline(steps: ProgressStep[], planItem?: string | null): string {
    const active = [...steps].reverse().find((s) => s.state === 'active')
    return active?.label ?? steps[steps.length - 1]?.label ?? planItem ?? 'Thinking'
}

/** "Worked for 12s" for a finished turn. */
export function workedFor(run: Pick<UiRun, 'startedAt' | 'finishedAt'> | undefined, steps = 0): string {
    const a = run?.startedAt ? Date.parse(run.startedAt) : NaN
    const b = run?.finishedAt ? Date.parse(run.finishedAt) : NaN
    if (Number.isFinite(a) && Number.isFinite(b) && b >= a) {
        const secs = Math.round((b - a) / 1000)
        if (secs < 1) return 'Worked for a moment'
        if (secs < 60) return `Worked for ${secs}s`
        const m = Math.floor(secs / 60)
        const r = secs % 60
        return r ? `Worked for ${m}m ${r}s` : `Worked for ${m}m`
    }
    return steps > 1 ? `Worked through ${steps} steps` : 'Worked on it'
}

/**
 * Plain words for a turn that didn't finish. The raw error stays in the run trace for developers.
 * `null` means say nothing (the creator stopped it, or the agent is just waiting on them).
 */
export function friendlyRunError(status: string | undefined): string | null {
    switch (status) {
        case 'failed': return "Something went wrong on my end and I couldn't finish that."
        case 'budget_exhausted': return 'That turned into a bigger job than I could finish in one go.'
        default: return null
    }
}
